// src/components/MonProfil.js
// "Mon profil" = uniquement ce qui concerne la PERSONNE :
// prénom, mot de passe, associations dont elle fait partie, installation, déconnexion.
// (L'équipe est dans l'onglet Équipe, l'association et la tombola dans Paramètres.)
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useProfil } from "../contexts/ProfilContext";
import { useOrg } from "../contexts/OrgContext";

export default function MonProfil({ onCreerAssociation }) {
  const { currentUser, deconnecter, changerMotDePasse, resetPassword, orgs, switchOrg, activeOrgId } = useAuth();
  const { profil, monPseudo, savePseudo } = useProfil();
  const { organisation } = useOrg();

  const [editMode, setEditMode] = useState(false);
  const [pseudo, setPseudo] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [showMdp, setShowMdp] = useState(false);
  const [mdpMode, setMdpMode] = useState("change");
  const [ancienMdp, setAncienMdp] = useState("");
  const [nouveauMdp, setNouveauMdp] = useState("");
  const [confirmMdp, setConfirmMdp] = useState("");
  const [savingMdp, setSavingMdp] = useState(false);

  useEffect(() => { setPseudo(profil?.pseudo || ""); }, [profil]);

  function showMsg(text, type = "success") {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3500);
  }

  async function handleSavePseudo(e) {
    e.preventDefault();
    if (!pseudo.trim()) return;
    setSaving(true);
    try {
      await savePseudo(pseudo);
      showMsg("✅ Prénom enregistré.");
      setEditMode(false);
    } catch { showMsg("Erreur lors de l'enregistrement.", "error"); }
    setSaving(false);
  }

  async function handleChangerMdp(e) {
    e.preventDefault();
    if (nouveauMdp !== confirmMdp) { showMsg("Les mots de passe ne correspondent pas.", "error"); return; }
    if (nouveauMdp.length < 8) { showMsg("8 caractères minimum.", "error"); return; }
    setSavingMdp(true);
    try {
      await changerMotDePasse(ancienMdp, nouveauMdp);
      showMsg("✅ Mot de passe modifié.");
      setAncienMdp(""); setNouveauMdp(""); setConfirmMdp("");
      setShowMdp(false);
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        showMsg("Ancien mot de passe incorrect.", "error");
      } else { showMsg("Erreur. Réessayez.", "error"); }
    }
    setSavingMdp(false);
  }

  async function handleForgotMdp() {
    setSavingMdp(true);
    try {
      await resetPassword(currentUser.email);
      showMsg("📧 Email de réinitialisation envoyé.");
      setShowMdp(false);
    } catch { showMsg("Erreur. Réessayez.", "error"); }
    setSavingMdp(false);
  }

  return (
    <div style={s.page}>
      <h2 style={s.title}>👤 Mon profil</h2>

      {msg && <div style={{ ...s.msg, ...(msg.type === "error" ? s.msgErr : s.msgOk) }}>{msg.text}</div>}

      {/* ── Identité ── */}
      <div style={s.identityCard}>
        {!editMode ? (
          <div style={s.identityRow}>
            <div style={s.avatar}>{monPseudo.charAt(0).toUpperCase()}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={s.displayName}>{monPseudo}</div>
              <div style={s.email}>{currentUser?.email}</div>
            </div>
            <button onClick={() => { setPseudo(monPseudo); setEditMode(true); }} style={s.btnOnCard}>✏️ Prénom</button>
          </div>
        ) : (
          <form onSubmit={handleSavePseudo} style={s.editForm}>
            <label style={s.labelWhite}>Votre prénom</label>
            <input type="text" value={pseudo} onChange={(e) => setPseudo(e.target.value)}
              maxLength={30} style={s.inputOnCard} autoFocus />
            <div style={s.editActions}>
              <button type="button" onClick={() => setEditMode(false)} style={s.btnOnCard}>Annuler</button>
              <button type="submit" disabled={saving} style={s.btnSaveOnCard}>{saving ? "…" : "Enregistrer"}</button>
            </div>
          </form>
        )}
      </div>

      {/* ── Mes associations ── */}
      <section style={s.section}>
        <h3 style={s.sectionTitle}>🏢 Mes associations</h3>
        <div style={s.list}>
          {orgs.map(({ org, role }) => {
            const active = org.id === activeOrgId;
            const nom = active ? (organisation?.nom || org.nom) : org.nom;
            return (
              <button key={org.id} onClick={() => !active && switchOrg(org.id)}
                style={{ ...s.orgRow, ...(active ? s.orgRowActive : {}) }}>
                <div style={s.orgAvatar}>{(nom || "?").charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <div style={s.orgNom}>{nom}</div>
                  <div style={s.orgRole}>{role === "admin" ? "👑 Admin" : "🤝 Bénévole"}</div>
                </div>
                {active ? <span style={s.orgActive}>✓ Active</span> : <span style={s.orgGo}>Ouvrir →</span>}
              </button>
            );
          })}
        </div>
        {onCreerAssociation && (
          <button onClick={onCreerAssociation} style={s.btnGhost}>+ Créer une autre association</button>
        )}
      </section>

      {/* ── Mot de passe ── */}
      <section style={s.section}>
        <button onClick={() => setShowMdp(!showMdp)} style={s.sectionToggle}>
          <span>🔒 Mot de passe</span><span>{showMdp ? "▲" : "▼"}</span>
        </button>
        {showMdp && (
          <div style={{ marginTop: "0.85rem" }}>
            <div style={s.tabs}>
              <button onClick={() => setMdpMode("change")} style={{ ...s.tab, ...(mdpMode === "change" ? s.tabActive : {}) }}>Changer</button>
              <button onClick={() => setMdpMode("forgot")} style={{ ...s.tab, ...(mdpMode === "forgot" ? s.tabActive : {}) }}>Oublié</button>
            </div>
            {mdpMode === "change" ? (
              <form onSubmit={handleChangerMdp} style={s.form}>
                <input type="password" placeholder="Mot de passe actuel" value={ancienMdp}
                  onChange={(e) => setAncienMdp(e.target.value)} required style={s.input} />
                <input type="password" placeholder="Nouveau (8 caractères min.)" value={nouveauMdp}
                  onChange={(e) => setNouveauMdp(e.target.value)} required style={s.input} />
                <input type="password" placeholder="Confirmer le nouveau" value={confirmMdp}
                  onChange={(e) => setConfirmMdp(e.target.value)} required style={s.input} />
                <button type="submit" disabled={savingMdp} style={s.btnPrimary}>{savingMdp ? "…" : "Changer le mot de passe"}</button>
              </form>
            ) : (
              <div style={s.form}>
                <p style={s.hint}>Un lien sera envoyé à <strong>{currentUser?.email}</strong>.</p>
                <button onClick={handleForgotMdp} disabled={savingMdp} style={s.btnPrimary}>{savingMdp ? "Envoi…" : "📧 Recevoir le lien"}</button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Installer l'application ── */}
      <section style={s.section}>
        <h3 style={s.sectionTitle}>📱 Installer l'application</h3>
        <p style={s.hint}>Accédez à TomboLive en un geste depuis votre écran d'accueil.</p>
        {[
          { titre: "🤖 Android (Chrome)", etapes: ["Ouvrez app.tombo.live dans Chrome", "Menu ⋮ puis « Ajouter à l'écran d'accueil »", "Confirmez"] },
          { titre: "🍎 iPhone / iPad (Safari)", etapes: ["Ouvrez app.tombo.live dans Safari", "Touchez l'icône de partage (↑)", "« Sur l'écran d'accueil » puis Ajouter"] },
        ].map((p) => (
          <div key={p.titre} style={s.installBox}>
            <div style={s.installTitre}>{p.titre}</div>
            {p.etapes.map((e, i) => (
              <div key={i} style={s.installStep}><span style={s.stepNum}>{i + 1}</span><span>{e}</span></div>
            ))}
          </div>
        ))}
      </section>

      <button onClick={deconnecter} style={s.btnLogout}>🚪 Se déconnecter</button>
    </div>
  );
}

const s = {
  page: { maxWidth: "520px", margin: "0 auto" },
  title: { fontSize: "1.25rem", fontWeight: "700", color: "#1a1a2e", margin: "0 0 1.25rem" },
  msg: { borderRadius: "10px", padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: "0.88rem" },
  msgOk: { background: "#e8f5e9", color: "#1b5e20" },
  msgErr: { background: "#ffebee", color: "#b71c1c" },
  identityCard: { background: "linear-gradient(135deg,#667eea,#764ba2)", borderRadius: "16px", padding: "1.1rem 1.25rem", marginBottom: "1rem" },
  identityRow: { display: "flex", alignItems: "center", gap: "12px" },
  avatar: { width: "50px", height: "50px", borderRadius: "50%", background: "rgba(255,255,255,0.25)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "800", fontSize: "1.35rem", flexShrink: 0 },
  displayName: { color: "#fff", fontWeight: "700", fontSize: "1.1rem" },
  email: { color: "rgba(255,255,255,0.8)", fontSize: "0.82rem", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis" },
  btnOnCard: { background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", borderRadius: "8px", padding: "6px 12px", cursor: "pointer", fontSize: "0.8rem", fontWeight: "600", flexShrink: 0 },
  btnSaveOnCard: { background: "#fff", border: "none", color: "#667eea", borderRadius: "8px", padding: "6px 14px", cursor: "pointer", fontSize: "0.8rem", fontWeight: "700" },
  editForm: { display: "flex", flexDirection: "column", gap: "8px" },
  labelWhite: { fontSize: "0.78rem", fontWeight: "600", color: "rgba(255,255,255,0.85)" },
  inputOnCard: { padding: "0.6rem 0.9rem", border: "1.5px solid rgba(255,255,255,0.45)", borderRadius: "8px", fontSize: "0.95rem", outline: "none", background: "rgba(255,255,255,0.15)", color: "#fff" },
  editActions: { display: "flex", gap: "8px", justifyContent: "flex-end" },
  section: { background: "#f7f8fc", borderRadius: "14px", padding: "1.1rem 1.25rem", marginBottom: "1rem", border: "1px solid #eee" },
  sectionTitle: { fontSize: "0.95rem", fontWeight: "700", color: "#333", margin: "0 0 0.75rem" },
  sectionToggle: { display: "flex", justifyContent: "space-between", width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: "0.95rem", fontWeight: "700", color: "#333" },
  hint: { fontSize: "0.84rem", color: "#888", lineHeight: 1.6, margin: "0 0 0.5rem" },
  list: { display: "flex", flexDirection: "column", gap: "8px", marginBottom: "10px" },
  orgRow: { display: "flex", alignItems: "center", gap: "10px", width: "100%", background: "#fff", border: "1.5px solid #eee", borderRadius: "12px", padding: "10px 12px", cursor: "pointer" },
  orgRowActive: { border: "1.5px solid #667eea", background: "#f5f7ff", cursor: "default" },
  orgAvatar: { width: "36px", height: "36px", borderRadius: "10px", background: "linear-gradient(135deg,#667eea,#764ba2)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "800", flexShrink: 0 },
  orgNom: { fontWeight: "700", color: "#1a1a2e", fontSize: "0.9rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  orgRole: { fontSize: "0.75rem", color: "#888", marginTop: "2px" },
  orgActive: { fontSize: "0.75rem", fontWeight: "700", color: "#4c5ee8", flexShrink: 0 },
  orgGo: { fontSize: "0.78rem", fontWeight: "600", color: "#667eea", flexShrink: 0 },
  btnGhost: { width: "100%", padding: "0.6rem", background: "#fff", border: "1.5px dashed #c7d2ff", color: "#667eea", borderRadius: "10px", cursor: "pointer", fontSize: "0.85rem", fontWeight: "600" },
  tabs: { display: "flex", gap: "6px", marginBottom: "0.75rem" },
  tab: { flex: 1, padding: "7px", background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: "8px", cursor: "pointer", fontSize: "0.82rem", fontWeight: "600", color: "#888" },
  tabActive: { border: "1.5px solid #667eea", color: "#667eea", background: "#eef0ff" },
  form: { display: "flex", flexDirection: "column", gap: "8px" },
  input: { padding: "0.65rem 0.9rem", border: "1.5px solid #e0e0e0", borderRadius: "10px", fontSize: "0.92rem", outline: "none", boxSizing: "border-box", background: "#fff", width: "100%" },
  btnPrimary: { padding: "0.7rem", background: "linear-gradient(135deg,#667eea,#764ba2)", color: "#fff", border: "none", borderRadius: "10px", fontWeight: "700", cursor: "pointer", fontSize: "0.9rem" },
  installBox: { background: "#fff", borderRadius: "10px", padding: "0.8rem 1rem", marginBottom: "8px", border: "1px solid #eee" },
  installTitre: { fontWeight: "700", color: "#1a1a2e", fontSize: "0.86rem", marginBottom: "6px" },
  installStep: { display: "flex", alignItems: "center", gap: "8px", fontSize: "0.82rem", color: "#555", marginBottom: "4px" },
  stepNum: { width: "20px", height: "20px", borderRadius: "50%", background: "linear-gradient(135deg,#667eea,#764ba2)", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: "700", fontSize: "0.7rem", flexShrink: 0 },
  btnLogout: { width: "100%", padding: "0.75rem", background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: "12px", fontWeight: "600", cursor: "pointer", fontSize: "0.9rem", color: "#555" },
};
